import * as React from "react";
import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { api } from "@/api/client";
import { LanguageMenuButton } from "@/components/language/LanguageMenuButton";
import {
  providerLabel,
  useAuthProviders,
  type AuthProvider,
} from "@/features/auth/authProviders";
import { PublishedLegalLinks } from "@/features/legal/LegalLinks";
import { nextAfterSignIn } from "@/lib/nextPath";
import { brand } from "@/theme";

type SignupInfo = {
  /** The deployment creates accounts on first sign-in. */
  self_service: boolean;
  workspace_domain: string | null;
  /** What a new organisation is enrolled on, or null when billing is off
   *  — promising a plan nobody is enrolled on would be a lie told to
   *  every visitor. ``renews``: the credits come back every month (Free)
   *  rather than once (a trial). */
  starter_plan: {
    name: string;
    credits: number;
    seats: number;
    renews: boolean;
  } | null;
};

/** Append a post-login redirect to a provider's ``login_url``.
 *
 * The OAuth ``/start`` endpoints and the dev bypass accept a
 * ``redirect=`` query param (clamped server-side to app-internal paths)
 * and replay it as the final post-login navigation. We carry it through
 * from the SPA's ``?next=`` so a user who opened a deep link or an
 * invitation signed out returns to that exact page after the SSO
 * round-trip. ``next`` has already been through ``nextAfterSignIn``. */
function withRedirect(loginUrl: string, next: string): string {
  const sep = loginUrl.includes("?") ? "&" : "?";
  return `${loginUrl}${sep}redirect=${encodeURIComponent(next)}`;
}

export function LoginPage(): React.JSX.Element {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const errorCode = searchParams.get("error");
  // The page to return to, only ever one of this app's.
  const next = nextAfterSignIn(searchParams.get("next"));
  const providers = useAuthProviders();
  // What a first-time visitor needs to know BEFORE signing in: whether
  // this deployment will make them an account, and what a new
  // organisation starts with. Public, like /auth/providers beside it.
  const signup = useQuery({
    queryKey: ["auth-signup-info"],
    queryFn: async () => (await api.get<SignupInfo>("/auth/signup-info")).data,
    retry: false,
  });

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", position: "relative" }}>
      {/* The language picker, before sign-in: the first page a new user
          sees should already speak their language. */}
      <Box sx={{ position: "absolute", top: 16, right: 16, zIndex: 1 }}>
        <LanguageMenuButton />
      </Box>
      {/* Left brand panel — dark monochrome, Repoworks wordmark + symbol watermark */}
      <Box
        sx={{
          flex: 1.1,
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "space-between",
          p: { md: 6, lg: 8 },
          // Flat: no gradients.
          background: brand.page,
          color: "#ffffff",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Stack direction="row" sx={{ alignItems: "center", gap: 1.25 }}>
          <Box
            component="img"
            src="/brand/repoworks_logo_white.svg"
            alt="Repoworks"
            sx={{ height: 36, width: "auto" }}
          />
        </Stack>
        <Stack sx={{ gap: 2.5, maxWidth: 520, position: "relative", zIndex: 1 }}>
          <Typography variant="h3" sx={{ fontWeight: 600, lineHeight: 1.1 }}>
            {t("auth.tagline")}
          </Typography>
          <Box
            sx={{
              width: 56,
              height: 4,
              borderRadius: 999,
              background: brand.muted,
            }}
          />
          <Typography sx={{ opacity: 0.88, maxWidth: 460, fontSize: 17 }}>
            {t(
              "auth.brand_pitch",
              "Chat with an agent that already knows your repositories. Export the answer as a branded PDF, DOCX, PPTX, or a reviewer-ready merge request.",
            )}
          </Typography>
          {/* Three-line "what you get" — answers the "is this the right
              place to sign in?" question before the reader types their
              password. Icons stay short-word so the list reads at a glance. */}
          <Stack sx={{ gap: 1, mt: 1, opacity: 0.92 }}>
            {[
              t("auth.pitch_chat", "Chat with any project you can read"),
              t("auth.pitch_export", "Export branded DOCX / PPTX / PDF artefacts"),
              t("auth.pitch_history", "Keep a history of every draft and iteration"),
            ].map((line) => (
              <Stack
                key={line}
                direction="row"
                sx={{ gap: 1.25, alignItems: "center" }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: 999,
                    bgcolor: brand.muted,
                    flexShrink: 0,
                  }}
                />
                <Typography sx={{ fontSize: 15, opacity: 0.92 }}>
                  {line}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
        <Stack direction="row" sx={{ alignItems: "center", gap: 1, opacity: 0.55 }}>
          <Typography variant="caption">{t("auth.powered_by", "Powered by")}</Typography>
          <Box
            component="img"
            src="/brand/appli_main_logo_1color_white.svg"
            alt="Appli"
            sx={{ height: 14 }}
          />
        </Stack>
        {/* Decorative watermark — glyph only (the filled tile mark reads as a
            stray panel at this size/opacity). */}
        <Box
          component="img"
          src="/brand/repoworks_glyph_white.svg"
          alt=""
          aria-hidden
          sx={{
            position: "absolute",
            right: -90,
            bottom: -60,
            width: 360,
            opacity: 0.18,
            pointerEvents: "none",
          }}
        />
      </Box>

      {/* Right form panel */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 3, md: 6 },
          bgcolor: brand.surface,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 420 }}>
          <Stack sx={{ gap: 3 }}>
            <Box>
              <Typography
                variant="overline"
                sx={{ color: brand.muted, letterSpacing: 1.6 }}
              >
                Repoworks
              </Typography>
              <Typography variant="h4" sx={{ mt: 0.5 }}>
                {/* The same button both signs you in and creates you, and
                    only the second audience needed telling. On a
                    deployment that accepts new accounts the old "Sign in"
                    left a first-time visitor with no sign they could get
                    one. */}
                {signup.data?.self_service
                  ? t("auth.title_self_service", "Sign in or create an account")
                  : t("auth.title", "Sign in")}
              </Typography>
              <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
                {signup.data?.self_service
                  ? t(
                      "auth.subtitle_self_service",
                      "Use any method below. If you have not been here before, signing in creates your account.",
                    )
                  : t("auth.subtitle")}
              </Typography>
            </Box>

            {/* What a NEW organisation gets, stated before signup rather
                than discovered after it. Read from the plan, so an
                operator editing it cannot leave this promising
                something else — including whether the credits come
                back every month or are a one-off. */}
            {signup.data?.self_service && signup.data.starter_plan && (
              <Alert severity="info" variant="outlined" data-testid="starter-plan-notice">
                {signup.data.starter_plan.renews
                  ? t(
                      "auth.free_plan_notice",
                      "New organisations start on {{plan}}: {{credits}} credits a month and {{seats}} user, free. No card needed — choose a paid plan when you want more.",
                      {
                        plan: signup.data.starter_plan.name,
                        credits: signup.data.starter_plan.credits,
                        seats: signup.data.starter_plan.seats,
                      },
                    )
                  : t(
                      "auth.trial_notice",
                      "New organisations start on {{plan}}: {{credits}} credits and {{seats}} user, free for a month. No card needed — choose a paid plan when you want more.",
                      {
                        plan: signup.data.starter_plan.name,
                        credits: signup.data.starter_plan.credits,
                        seats: signup.data.starter_plan.seats,
                      },
                    )}
              </Alert>
            )}

            {errorCode && (
              <Alert
                severity="warning"
                variant="outlined"
                onClose={() => {
                  searchParams.delete("error");
                  setSearchParams(searchParams, { replace: true });
                }}
              >
                {translateAuthError(t, errorCode, searchParams.get("provider"))}
              </Alert>
            )}

            {providers.isLoading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {providers.isSuccess && providers.data.length === 0 && (
              <Alert severity="warning" variant="outlined">
                {t(
                  "auth.no_provider",
                  "No authentication provider is configured. Ask your admin to enable one.",
                )}
              </Alert>
            )}

            {providers.isError && (
              <Alert severity="error" variant="outlined">
                {t(
                  "auth.provider_fetch_failed",
                  "Could not reach the auth service. Check your network and try again.",
                )}
              </Alert>
            )}

            {(providers.data ?? []).map((provider, index) => (
              <ProviderForm
                key={provider.name}
                provider={provider}
                divider={index > 0}
                next={next}
              />
            ))}

            <PublishedLegalLinks docs={["terms", "privacy"]} sx={{ justifyContent: "center", pt: 1 }} />
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}

/** The sign-in providers a provider_unavailable error can name; their
 *  names are brands, the same in every language. */
const PROVIDER_NAMES: Readonly<Record<string, string>> = {
  google: "Google",
  github: "GitHub",
  microsoft: "Microsoft",
};

/** Map a machine error code (from ?error= or an LDAP 4xx detail) to a
 *  translated message. Unknown codes get the generic line — raw codes
 *  must never reach the user. */
function translateAuthError(t: TFunction, code: string, provider?: string | null): string {
  if (code === "provider_unavailable") {
    // Own keys only: ?provider=__proto__ must not reach Object.prototype.
    const name = provider && Object.hasOwn(PROVIDER_NAMES, provider) ? PROVIDER_NAMES[provider] : undefined;
    return name
      ? t("auth.errors.provider_unavailable", { provider: name })
      : t("auth.errors.provider_unavailable_unnamed");
  }
  return t(`auth.errors.${code}`, {
    defaultValue: t("auth.errors.generic"),
  });
}

function ProviderForm({
  provider,
  divider,
  next,
}: {
  provider: AuthProvider;
  divider: boolean;
  next: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  if (provider.kind === "oauth") {
    return (
      <>
        {divider && <Divider sx={{ color: "text.secondary" }}>{t("auth.or", "or")}</Divider>}
        <Button size="large" href={withRedirect(provider.login_url, next)}>
          {providerLabel(t, provider)}
        </Button>
      </>
    );
  }
  if (provider.kind === "dev") {
    return (
      <>
        {divider && <Divider sx={{ color: "text.secondary" }}>{t("auth.or", "or")}</Divider>}
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          href={withRedirect(provider.login_url, next)}
        >
          {providerLabel(t, provider)}
        </Button>
      </>
    );
  }
  return (
    <>
      {divider && <Divider sx={{ color: "text.secondary" }}>{t("auth.or", "or")}</Divider>}
      <LdapForm provider={provider} next={next} />
    </>
  );
}

function LdapForm({
  provider,
  next,
}: {
  provider: AuthProvider;
  next: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const response = await api.post<{ signup_required?: boolean; redirect?: string }>(
        provider.login_url.replace(/^\/api/, ""),
        { username, password, redirect: next },
      );
      if (response.status >= 400) throw new Error("login_failed");
      // A first sign-in has no account yet: the terms step comes first.
      window.location.assign(
        response.data?.signup_required && response.data.redirect ? response.data.redirect : next,
      );
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })
        .response?.data?.detail;
      setError(translateAuthError(t, detail ?? "generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack component="form" sx={{ gap: 2 }} onSubmit={submit}>
      <Typography variant="overline" color="text.secondary">
        {providerLabel(t, provider)}
      </Typography>
      {error && (
        <Alert severity="error" variant="outlined">
          {error}
        </Alert>
      )}
      <TextField
        size="small"
        label={t("auth.username_label")}
        autoComplete="username"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        fullWidth
      />
      <TextField
        size="small"
        type="password"
        label={t("auth.password_label")}
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        fullWidth
      />
      <Button
        type="submit"
        size="large"
        disabled={busy || !username || !password}
      >
        {busy ? t("auth.signing_in", "Signing in…") : t("auth.sign_in", "Sign in")}
      </Button>
    </Stack>
  );
}
